import { useEffect, useRef, useState } from 'react';
import { HomeBox, HomeBoxPayload } from '@/services/home-boxes.service';
import { productService } from '@/services/product.service';
import { Product } from '@/types/product.types';
import '@/styles/dashboard.css';

const emptyForm: HomeBoxPayload = {
  title: '',
  description: '',
  badge: '',
  background_image_url: '',
  status: 'published',
  display_order: 0,
  product_ids: [],
};

type HomeBoxesService = {
  getAllAdmin: () => Promise<HomeBox[]>;
  create: (payload: HomeBoxPayload) => Promise<HomeBox>;
  update: (id: number, payload: HomeBoxPayload) => Promise<HomeBox>;
  delete: (id: number) => Promise<void>;
};

type HomeBoxesAdminProps = {
  pageTitle: string;
  boxesService: HomeBoxesService;
};

export const HomeBoxesAdmin = ({ pageTitle, boxesService }: HomeBoxesAdminProps) => {
  const [boxes, setBoxes] = useState<HomeBox[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [editing, setEditing] = useState<HomeBox | null>(null);
  const [form, setForm] = useState<HomeBoxPayload>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [boxesData, productsData] = await Promise.all([
        boxesService.getAllAdmin(),
        productService.getAll(),
      ]);
      setBoxes(boxesData);
      setProducts(productsData.filter((p) => p.status === 'published'));
    } catch (error) {
      console.error('Erreur chargement boxes admin:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
      };
      if (editing) {
        await boxesService.update(editing.id, payload);
      } else {
        await boxesService.create(payload);
      }
      setEditing(null);
      setForm(emptyForm);
      await loadData();
    } catch (error) {
      console.error('Erreur sauvegarde box:', error);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError('Le fichier selectionne doit etre une image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image trop lourde (max 5 MB).');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      setForm((prev) => ({ ...prev, background_image_url: result }));
      setImageError('');
    };
    reader.readAsDataURL(file);
  };

  return (
    <>
    <div className="table-card" style={{ padding: 20 }}>
              <h2 className="table-title">{pageTitle}</h2>

              <form onSubmit={save} className="grid gap-3" style={{ marginTop: 16 }}>
                <input
                  className="form-input"
                  placeholder="Titre (optionnel)"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
                <input className="form-input" placeholder="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                <input className="form-input" placeholder="Badge" value={form.badge || ''} onChange={(e) => setForm({ ...form, badge: e.target.value })} />
                <div>
                  <label className="form-label">Image de fond</label>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                  />
                  <div className="flex gap-2" style={{ marginBottom: 8 }}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => imageInputRef.current?.click()}>
                      Uploader une image
                    </button>
                    {form.background_image_url && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setForm({ ...form, background_image_url: '' })}
                      >
                        Supprimer image
                      </button>
                    )}
                  </div>
                  {imageError && <small style={{ color: '#dc2626' }}>{imageError}</small>}
                  {form.background_image_url && (
                    <div style={{ marginTop: 8, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', maxWidth: 320 }}>
                      <img
                        src={form.background_image_url}
                        alt="Apercu image box"
                        style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as 'draft' | 'published' })}>
                    <option value="published">Publié</option>
                    <option value="draft">Brouillon</option>
                  </select>
                  <input className="form-input" type="number" placeholder="Ordre" value={form.display_order || 0} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || '0', 10) })} />
                </div>

                <label className="form-label">Produits liés</label>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, maxHeight: 180, overflow: 'auto' }}>
                  {products.map((p) => {
                    const checked = form.product_ids.includes(p.id);
                    return (
    <>

                      <label key={p.id} style={{ display: 'block', marginBottom: 6 }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setForm({
                              ...form,
                              product_ids: checked ? form.product_ids.filter((id) => id !== p.id) : [...form.product_ids, p.id],
                            })
                          }
                        />
                        <span style={{ marginLeft: 8 }}>{p.name}</span>
                      </label>
                    
    </>
  );
                  })}
                </div>

                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary">{editing ? 'Mettre à jour' : 'Créer'}</button>
                  {editing && (
                    <button type="button" className="btn btn-secondary" onClick={() => { setEditing(null); setForm(emptyForm); setImageError(''); }}>
                      Annuler
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="table-card" style={{ marginTop: 16, padding: 20 }}>
              <h3 className="table-title">Boxes existants</h3>
              {loading ? (
                <p>Chargement...</p>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {boxes.map((box) => (
                    <div key={box.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>{box.title?.trim() ? box.title : '(Sans titre)'}</strong>
                        <p style={{ margin: '4px 0' }}>{box.description}</p>
                        <small>{box.product_ids.length} produit(s) - {box.status}</small>
                      </div>
                      <div className="flex gap-2">
                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(box); setForm({ title: box.title, description: box.description || '', badge: box.badge || '', background_image_url: box.background_image_url || '', slug: box.slug, status: box.status, display_order: box.display_order, product_ids: box.product_ids || [] }); }}>
                          Modifier
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={async () => { await boxesService.delete(box.id); await loadData(); }}>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
    </>
  );
};
